import type { ReactNode } from 'react';
import { Link } from 'components/Link';
import { cn } from 'services/cn';

/**
 * Prototype palette (v2-cloud-merge / chat3):
 * - article root: gap-48
 * - section group: gap-24
 * - body text: text-headline leading-26 neutrals-800 / dark neutrals-400
 * - strong emphasis: neutrals-1200 / dark white
 * - links: blue-700 / dark blue-400 underline
 * - callout / step / list-card surface: bg-neutrals-100 / dark bg-neutrals-1100 + border-neutrals-200 / dark border-neutrals-900
 * - blue info card: bg-blue-100 / dark bg-blue-1100 + border-blue-300 / dark border-blue-900
 * - step label: text-note font-medium uppercase tracking-wider blue-700 / dark blue-400
 */
export const blogClass = {
    root: 'tw:flex tw:flex-col tw:gap-48 tw:min-w-0 tw:flex-1',
    section: 'tw:flex tw:flex-col tw:gap-24',
    h2: 'tw:text-headline-l tw:font-medium tw:text-neutrals-1200 tw:dark:text-white tw:m-0',
    h3: 'tw:text-headline tw:font-medium tw:text-neutrals-1200 tw:dark:text-white tw:m-0',
    paragraph: 'tw:text-headline tw:leading-26 tw:text-neutrals-800 tw:dark:text-neutrals-400',
    paragraphStrong: 'tw:text-headline tw:leading-26 tw:text-neutrals-1200 tw:dark:text-white',
    strong: 'tw:text-neutrals-1200 tw:dark:text-white',
    link: 'tw:inline tw:text-blue-700 tw:dark:text-blue-400 tw:underline',
    linkMedium: 'tw:inline tw:text-blue-700 tw:dark:text-blue-400 tw:underline tw:font-medium',
    cardSurface:
        'tw:p-24 tw:rounded-md tw:bg-neutrals-100 tw:dark:bg-neutrals-1100 tw:border tw:border-neutrals-200 tw:dark:border-neutrals-900',
    cardSurfaceOutline: 'tw:p-24 tw:rounded-md tw:border tw:border-neutrals-200 tw:dark:border-neutrals-900',
    infoCardSurface:
        'tw:p-24 tw:rounded-md tw:bg-blue-100 tw:dark:bg-blue-1100 tw:border tw:border-blue-300 tw:dark:border-blue-900',
    stepLabel: 'tw:text-note tw:font-medium tw:uppercase tw:tracking-wider tw:text-blue-700 tw:dark:text-blue-400',
    imagePlaceholder:
        'tw:aspect-16/9 tw:rounded-md tw:bg-neutrals-200 tw:dark:bg-neutrals-1000 tw:flex tw:items-center tw:justify-center tw:border tw:border-neutrals-300 tw:dark:border-neutrals-900',
    image: 'tw:w-full tw:h-auto tw:rounded-md tw:border tw:border-neutrals-200 tw:dark:border-neutrals-900',
    bullet: 'tw:flex-none tw:w-6 tw:h-6 tw:rounded-full tw:bg-blue-700 tw:dark:bg-blue-400 tw:mt-8',
    footer: 'tw:flex tw:items-center tw:justify-between tw:gap-20 tw:pt-20 tw:border-t tw:border-neutrals-200 tw:dark:border-neutrals-900',

    /* ---- Legacy keys kept so existing posts still compile. ---- */
    h2First: 'tw:text-headline-l tw:font-medium tw:text-neutrals-1200 tw:dark:text-white tw:m-0',
    h4: 'tw:text-headline tw:font-medium tw:text-neutrals-1200 tw:dark:text-white tw:m-0',
    card: 'tw:p-24 tw:rounded-md tw:bg-neutrals-100 tw:dark:bg-neutrals-1100 tw:border tw:border-neutrals-200 tw:dark:border-neutrals-900',
    cardSm: 'tw:p-24 tw:rounded-md tw:bg-neutrals-100 tw:dark:bg-neutrals-1100 tw:border tw:border-neutrals-200 tw:dark:border-neutrals-900',
    callout:
        'tw:p-24 tw:rounded-md tw:bg-neutrals-100 tw:dark:bg-neutrals-1100 tw:border-l-4 tw:border-blue-700 tw:dark:border-blue-400',
    calloutText: 'tw:text-headline tw:leading-26 tw:text-neutrals-1200 tw:dark:text-white tw:font-medium',
    cardTitle: 'tw:text-headline tw:font-medium tw:text-blue-700 tw:dark:text-blue-400 tw:mb-12',
    faqList: 'tw:flex tw:flex-col tw:gap-16',
    faqTitle: 'tw:text-headline tw:font-medium tw:text-blue-700 tw:dark:text-blue-400 tw:mb-12',
    highlight: 'tw:text-blue-700 tw:dark:text-blue-400 tw:font-medium',
    highlightLg: 'tw:text-lg tw:text-blue-700 tw:dark:text-blue-400 tw:font-medium tw:mt-4',
    emphasis: 'tw:text-neutrals-1200 tw:dark:text-white tw:font-medium',
    list: 'tw:flex tw:flex-col tw:gap-12',
    listSm: 'tw:flex tw:flex-col tw:gap-8',
    listNested: 'tw:flex tw:flex-col tw:gap-12 tw:mt-8',
    listPlain: 'tw:flex tw:flex-col tw:gap-12 tw:list-none',
    listItem: 'tw:text-neutrals-800 tw:dark:text-neutrals-400',
    listItemRow: 'tw:flex tw:items-center tw:gap-12',
    listAccentItem: 'tw:text-blue-700 tw:dark:text-blue-400 tw:font-medium',
    grid2: 'tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-12 tw:my-12',
    grid3: 'tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-12 tw:my-12',
    grid4: 'tw:grid tw:grid-cols-2 tw:md:grid-cols-4 tw:gap-8 tw:my-12',
    gridItem:
        'tw:p-20 tw:rounded-md tw:bg-neutrals-100 tw:dark:bg-neutrals-1100 tw:border tw:border-neutrals-200 tw:dark:border-neutrals-900 tw:text-center',
    gridItemLabel: 'tw:text-headline tw:font-medium tw:text-blue-700 tw:dark:text-blue-400',
    stepBadge:
        'tw:bg-blue-700 tw:dark:bg-blue-400 tw:text-white tw:font-medium tw:rounded-full tw:w-32 tw:h-32 tw:flex tw:items-center tw:justify-center tw:shrink-0',
    cta: 'tw:p-32 tw:rounded-md tw:bg-blue-100 tw:dark:bg-blue-900/20 tw:border tw:border-blue-600 tw:dark:border-blue-700 tw:mt-12 tw:text-center',
    ctaTitle: 'tw:text-headline-l tw:font-medium tw:text-neutrals-1200 tw:dark:text-white tw:mb-12',
    codeBlock:
        'tw:p-12 tw:rounded-md tw:bg-neutrals-100 tw:dark:bg-neutrals-1100 tw:border tw:border-neutrals-200 tw:dark:border-neutrals-900 tw:text-body',
    tag: 'tw:px-12 tw:py-8 tw:rounded-md tw:bg-neutrals-100 tw:dark:bg-neutrals-1100 tw:text-blue-700 tw:dark:text-blue-400 tw:font-medium tw:border tw:border-neutrals-200 tw:dark:border-neutrals-900',
} as const;

/** Article root — outer `<article>` for the post body. */
export const BlogRoot = ({ children, className }: { children: ReactNode; className?: string }) => (
    <article className={cn(blogClass.root, className)}>{children}</article>
);

/** Section wrapper — groups H2 + paragraphs/cards. Use one per H2 in the prototype. */
export const BlogSection = ({ children, className }: { children: ReactNode; className?: string }) => (
    <section className={cn(blogClass.section, className)}>{children}</section>
);

export const BlogH2 = ({ children, className }: { children: ReactNode; className?: string }) => (
    <div className={cn(blogClass.h2, className)}>{children}</div>
);

export const BlogH3 = ({ children, className }: { children: ReactNode; className?: string }) => (
    <h3 className={cn(blogClass.h3, className)}>{children}</h3>
);

/** Body paragraph styled like prototype: text-headline / leading-26 / muted body color. */
export const BlogParagraph = ({
    children,
    emphasis,
    className,
}: {
    children: ReactNode;
    emphasis?: boolean;
    className?: string;
}) => <div className={cn(emphasis ? blogClass.paragraphStrong : blogClass.paragraph, className)}>{children}</div>;

/** Inline emphasis — neutrals-1200 / dark white inside a body paragraph. */
export const BlogStrong = ({ children }: { children: ReactNode }) => (
    <strong className={blogClass.strong}>{children}</strong>
);

export const BlogLink = ({
    to,
    children,
    isExternal = true,
    medium,
}: {
    to: string;
    children: ReactNode;
    isExternal?: boolean;
    medium?: boolean;
}) => (
    <Link to={to} isExternal={isExternal} className={medium ? blogClass.linkMedium : blogClass.link}>
        {children}
    </Link>
);

/** Outlined container with dot bullets (matches prototype "Why File Security…" list). */
export const BlogBulletList = ({ children, className }: { children: ReactNode; className?: string }) => (
    <div className={cn(blogClass.cardSurfaceOutline, 'tw:flex tw:flex-col tw:gap-20', className)}>{children}</div>
);

export const BlogBulletItem = ({ children }: { children: ReactNode }) => (
    <div className='tw:flex tw:gap-12 tw:items-start'>
        <span className={blogClass.bullet} />
        <div className={blogClass.paragraph}>{children}</div>
    </div>
);

/**
 * Generic surface card (neutral-100 / dark neutrals-1100) — use for quotes,
 * outcome blurbs, partnership bullets, "Chat3 understood…" callouts.
 */
export const BlogCard = ({
    children,
    className,
    tone = 'neutral',
}: {
    children: ReactNode;
    className?: string;
    tone?: 'neutral' | 'info';
}) => (
    <div
        className={cn(
            tone === 'info' ? blogClass.infoCardSurface : blogClass.cardSurface,
            'tw:flex tw:flex-col tw:gap-24',
            className,
        )}
    >
        {children}
    </div>
);

/** Single-line highlight inside a surface card. */
export const BlogHighlightCard = ({
    children,
    tone = 'neutral',
}: {
    children: ReactNode;
    tone?: 'neutral' | 'info';
}) => (
    <BlogCard tone={tone}>
        <div className={blogClass.paragraphStrong}>{children}</div>
    </BlogCard>
);

/** Step label — short uppercase blue caption above a step's description. */
export const BlogStepLabel = ({ children }: { children: ReactNode }) => (
    <div className={blogClass.stepLabel}>{children}</div>
);

/**
 * One step inside a step card: label + body + optional image (URL or
 * `placeholder` for an empty placeholder box).
 */
export const BlogStep = ({
    label,
    children,
    image,
    placeholder,
    imageAlt,
}: {
    label: ReactNode;
    children: ReactNode;
    image?: string;
    placeholder?: boolean;
    imageAlt?: string;
}) => (
    <div className='tw:flex tw:flex-col tw:gap-12'>
        <BlogStepLabel>{label}</BlogStepLabel>
        <div className={blogClass.paragraph}>{children}</div>
        {image ? <img src={image} alt={imageAlt ?? ''} className={blogClass.image} /> : null}
        {placeholder && !image ? (
            <div className={blogClass.imagePlaceholder} role='img' aria-label={imageAlt ?? 'placeholder'}>
                <svg
                    width='48'
                    height='48'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='tw:text-neutrals-600 tw:dark:text-neutrals-700'
                    aria-hidden='true'
                >
                    <rect x='3' y='3' width='18' height='18' rx='2' />
                    <circle cx='9' cy='9' r='2' />
                    <path d='M21 15l-5-5L5 21' />
                </svg>
            </div>
        ) : null}
    </div>
);

/**
 * Step card: H3 title + optional intro + steps + optional outcome line.
 * Matches the two “real workflows” cards in the prototype.
 */
export const BlogStepCard = ({
    title,
    intro,
    children,
    outcome,
}: {
    title: ReactNode;
    intro?: ReactNode;
    children: ReactNode;
    outcome?: ReactNode;
}) => (
    <div className={cn(blogClass.cardSurface, 'tw:flex tw:flex-col tw:gap-24')}>
        <BlogH3>{title}</BlogH3>
        {intro ? <div className={blogClass.paragraph}>{intro}</div> : null}
        <div className='tw:flex tw:flex-col tw:gap-16'>{children}</div>
        {outcome ? <div className={blogClass.paragraphStrong}>{outcome}</div> : null}
    </div>
);

/** Image rendered inside a surface card (used for in-flow figures). */
export const BlogImage = ({ src, alt, caption }: { src: string; alt: string; caption?: ReactNode }) => (
    <div className='tw:flex tw:flex-col tw:gap-12'>
        <img src={src} alt={alt} className={blogClass.image} />
        {caption ? <div className='tw:text-body tw:text-neutrals-700 tw:dark:text-neutrals-500'>{caption}</div> : null}
    </div>
);

/** List of card-shaped bullets (matches "Increased trust:" partnership block). */
export const BlogCardList = ({ children }: { children: ReactNode }) => (
    <div className='tw:flex tw:flex-col tw:gap-16'>{children}</div>
);

export const BlogCardListItem = ({ children }: { children: ReactNode }) => (
    <div className={blogClass.cardSurface}>
        <div className={blogClass.paragraph}>{children}</div>
    </div>
);

/* ---- Legacy aliases kept so existing posts compile without churn. ---- */

/** Legacy alias — use BlogCard for new code. */
export const BlogCardSm = ({ children }: { children: ReactNode }) => <BlogCard>{children}</BlogCard>;

/** Legacy alias — use BlogCard for new code. */
export const BlogCallout = ({ children }: { children: ReactNode }) => <BlogCard>{children}</BlogCard>;

/** Legacy alias — FAQ list keeps simple stacked layout. */
export const BlogFaqList = ({ children }: { children: ReactNode }) => (
    <div className='tw:flex tw:flex-col tw:gap-16'>{children}</div>
);

// Collapsible FAQ item — native <details>/<summary> accordion styled in blog-html.css
// (`details.blog-faq`), so existing static posts and legacy `faq` blocks match the new
// WYSIWYG FAQ. Collapsed by default; click the question to expand the answer.
export const BlogFaqItem = ({ title, children }: { title: ReactNode; children: ReactNode }) => (
    <details className='blog-faq'>
        <summary>{title}</summary>
        <div>{children}</div>
    </details>
);

export const BlogFaqIntro = ({ title }: { title: ReactNode }) => <BlogH2>{title}</BlogH2>;

export const BlogHighlight = ({ children, large }: { children: ReactNode; large?: boolean }) => (
    <BlogParagraph emphasis className={large ? 'tw:text-lg' : undefined}>
        {children}
    </BlogParagraph>
);

/* ---- Legacy list API — preserved so existing posts still compile. ---- */

export const BlogListItem = ({
    children,
    className,
    accent,
    row,
}: {
    children: ReactNode;
    className?: string;
    accent?: boolean;
    row?: boolean;
}) => (
    <BlogBulletItem>
        <span
            className={cn(
                accent && 'tw:text-blue-700 tw:dark:text-blue-400 tw:font-medium',
                row && 'tw:flex tw:items-center tw:gap-12',
                className,
            )}
        >
            {children}
        </span>
    </BlogBulletItem>
);

type BlogListVariant = 'bullet' | 'plain' | 'nested';

type BlogListProps =
    | {
          items: ReactNode[];
          accent?: boolean;
          children?: never;
          className?: never;
          size?: never;
          variant?: never;
      }
    | {
          children: ReactNode;
          items?: never;
          accent?: never;
          className?: string;
          size?: 'md' | 'sm';
          variant?: BlogListVariant;
      };

export const BlogList = (props: BlogListProps) => {
    if ('items' in props) {
        const { items, accent } = props;
        return (
            <BlogBulletList>
                {(items ?? []).map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <BlogBulletItem key={index}>
                        <span className={accent ? 'tw:text-blue-700 tw:dark:text-blue-400 tw:font-medium' : undefined}>
                            {item}
                        </span>
                    </BlogBulletItem>
                ))}
            </BlogBulletList>
        );
    }

    return <BlogBulletList className={props.className}>{props.children}</BlogBulletList>;
};
